import { c as _c } from "react/compiler-runtime";
import { SphereDropzone } from "@components/common/sphere-dropzone/sphere-dropzone";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { UploadElementType } from "@custom-types/file-upload-types";
import { FileDocumentIcon } from "@flat-ui";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { MARKUP_ATTACHMENT_EXTENSIONS_WHITE_LIST } from "@pages/project-details/project-markups/markup-attachment-utils";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { markupAttachmentGroupSelector } from "@store/i-elements/i-elements-selectors";
import { setIElements } from "@store/i-elements/i-elements-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { cloneDeep } from "es-toolkit/compat";
import { PALETTE_VARS } from "@styles/palette-vars";

/** Maximum file size of attachment can be uploaded for annotations */
const MAX_ATTACHMENT_SIZE_IN_MB = 1024;

/**
 * Returns the attachment groups with the new attachments added. A mutation carries the group
 * it creates when the markup had none. The attachments themselves are not kept in the store,
 * but their groups are, since they hold the attachments count of the markup.
 */
function getUpdatedAttachmentGroups(mutations, existingGroup, userId) {
  const groups = new Map();
  if (existingGroup) {
    groups.set(existingGroup.id, existingGroup);
  }
  for (const {
    group,
    newElement,
    createdAt
  } of mutations) {
    const groupId = group?.id ?? newElement.parentId;
    if (!groupId) {
      continue;
    }
    const current = groups.get(groupId) ?? (group && {
      ...group,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      root_Id: group.rootId,
      createdBy: userId,
      createdAt,
      modifiedBy: userId,
      modifiedAt: createdAt
    });
    if (current) {
      groups.set(groupId, {
        ...current,
        childrenIds: [...(current.childrenIds ?? []), newElement.id]
      });
    }
  }
  return [...groups.values()];
}

/** Renders dropzone to upload markup attachment  */
export function MarkupUploadAttachment(t0) {
  const $ = _c(27);
  const {
    markup
  } = t0;
  const {
    trackEvent
  } = useTrackEvent();
  const {
    projectId,
    setIsMarkupUpdating,
    isAttachmentUploading,
    setIsAttachmentUploading,
    attachments,
    updateAttachments
  } = useMarkupContext();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    addAttachmentsToMarkup
  } = useProjectMarkupUpdate(t1);
  let t2;
  if ($[2] !== markup) {
    t2 = markupAttachmentGroupSelector(markup);
    $[2] = markup;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const attachmentGroup = useAppSelector(t2);
  const currentUser = useAppSelector(currentUserSelector);
  const {
    refreshAttachmentIfExpired
  } = useRefreshAttachment();
  const dispatch = useAppDispatch();
  let t3;
  if ($[4] !== setIsAttachmentUploading || $[5] !== setIsMarkupUpdating) {
    t3 = function setIsLoading(isLoading) {
      setIsAttachmentUploading(isLoading);
      setIsMarkupUpdating(isLoading);
    };
    $[4] = setIsAttachmentUploading;
    $[5] = setIsMarkupUpdating;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setIsLoading = t3;
  let t4;
  if ($[7] !== addAttachmentsToMarkup || $[8] !== attachmentGroup || $[9] !== attachments || $[10] !== currentUser || $[11] !== dispatch || $[12] !== markup || $[13] !== refreshAttachmentIfExpired || $[14] !== setIsLoading || $[15] !== trackEvent || $[16] !== updateAttachments) {
    t4 = async function addAttachment(uploadedResponse) {
      const uploadedFiles = uploadedResponse.successful;
      if (!uploadedFiles.length) {
        return;
      }
      trackEvent({
        name: AnnotationEvents.addNewAttachment,
        props: {
          numberOfFiles: uploadedFiles.length,
          iElementType: markup.type,
          ...(markup.typeHint && {
            iElementTypeHint: markup.typeHint
          })
        }
      });
      setIsLoading(true);
      const mutations = await addAttachmentsToMarkup({
        uploadedFiles,
        markup,
        attachmentGroup
      });
      if (!mutations || !currentUser) {
        return setIsLoading(false);
      }
      dispatch(setIElements(getUpdatedAttachmentGroups(mutations, attachmentGroup, currentUser.identity)));
      try {
        const clonedAttachments = cloneDeep(attachments);
        const attachmentIds = mutations.map(_temp);
        const refreshedAttachments = await refreshAttachmentIfExpired(attachmentIds);
        const finalUpdatedAttachments = [...clonedAttachments, ...refreshedAttachments];
        updateAttachments(finalUpdatedAttachments);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };
    $[7] = addAttachmentsToMarkup;
    $[8] = attachmentGroup;
    $[9] = attachments;
    $[10] = currentUser;
    $[11] = dispatch;
    $[12] = markup;
    $[13] = refreshAttachmentIfExpired;
    $[14] = setIsLoading;
    $[15] = trackEvent;
    $[16] = updateAttachments;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const addAttachment = t4;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SphereAvatar icon={<FileDocumentIcon sx={{
      width: "50px",
      height: "50px"
    }} />} size="x-large" shouldHideWhiteRim={true} iconColor={PALETTE_VARS.text.secondary} backgroundColor={PALETTE_VARS.surfaces.tertiary} />;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== markup.id || $[20] !== projectId) {
    t6 = {
      uploadElementType: UploadElementType.annotation,
      projectId,
      iElementId: markup.id
    };
    $[19] = markup.id;
    $[20] = projectId;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== addAttachment || $[23] !== isAttachmentUploading || $[24] !== setIsAttachmentUploading || $[25] !== t6) {
    t7 = <SphereDropzone titleDragAndDrop="New Attachment" instruction="Drag & drop here" maxFileSize={MAX_ATTACHMENT_SIZE_IN_MB} hasUploadFileButton={false} shouldShowSupportedFormats={false} shouldAllowMultiUpload={true} avatar={t5} allowedExtensions={MARKUP_ATTACHMENT_EXTENSIONS_WHITE_LIST} isLoading={isAttachmentUploading} setIsLoading={setIsAttachmentUploading} onUploadComplete={addAttachment} context={t6} />;
    $[22] = addAttachment;
    $[23] = isAttachmentUploading;
    $[24] = setIsAttachmentUploading;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function _temp(mutations_0) {
  return mutations_0.newElement.id;
}